// STLViewer.js
import React, { Component, createRef } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three-stdlib';
import { OrbitControls } from 'three-stdlib';

class STLViewer extends Component {
    constructor(props) {
        super(props);
        this.mountRef = createRef();
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.mesh = null;
    }

    componentDidMount() {
        this.initThree();
        window.addEventListener("resize", this.handleResize);

    }

    componentDidUpdate(prevProps) {
        const { url, geometry } = this.props;
        if (url !== prevProps.url || geometry !== prevProps.geometry) {
            this.clearScene();
            this.loadGeometry();
        }
        else if (
            this.props.rotation_x !== prevProps.rotation_x ||
            this.props.rotation_y !== prevProps.rotation_y ||
            this.props.rotation_z !== prevProps.rotation_z
        ) {
            if (this.mesh) {
                this.mesh.rotation.x = (-Math.PI / 2) + THREE.MathUtils.degToRad(this.props.rotation_x);
                this.mesh.rotation.y = THREE.MathUtils.degToRad(this.props.rotation_y);
                this.mesh.rotation.z = THREE.MathUtils.degToRad(this.props.rotation_z);
                this.centerAndDropMesh(this.mesh)
            }
        }

    }
    centerAndDropMesh(mesh) {
        // Compute bounding box *after* rotation and scale are applied, in world space
        const box = new THREE.Box3().setFromObject(mesh);

        // Calculate center in X and Z (world space)
        const centerX = (box.max.x + box.min.x) / 2;
        const centerZ = (box.max.z + box.min.z) / 2;

        // Calculate bottom Y (lowest point)
        const minY = box.min.y;

        // Offset mesh position to center on X, Z and rest on Y=0
        mesh.position.x -= centerX;
        mesh.position.z -= centerZ;
        mesh.position.y -= minY;
    }



    componentWillUnmount() {
        if (this.renderer && this.mountRef.current) {
            this.mountRef.current.removeChild(this.renderer.domElement);
        }
        window.removeEventListener("resize", this.handleResize);

    }
    handleResize = () => {
        const width = this.mountRef.current.clientWidth;
        const height = this.mountRef.current.clientHeight;
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
    };

    initThree() {
        const width = this.mountRef.current.clientWidth;
        const height = this.mountRef.current.clientHeight;

        this.scene = new THREE.Scene();

        this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        this.camera.position.z = 3;

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(width, height);
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.mountRef.current.appendChild(this.renderer.domElement);

        // Add Ambient Light for overall brightness
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7); // White light, 70% intensity
        this.scene.add(ambientLight);

        // Increase directional light intensity and add second directional light for better shading
        const directionalLight1 = new THREE.DirectionalLight(0xffffff, 2);
        directionalLight1.position.set(1, 1, 1).normalize();
        this.scene.add(directionalLight1);

        const directionalLight2 = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight2.position.set(-1, 1, 1).normalize();
        this.scene.add(directionalLight2);
        // Create a light that follows the camera direction
        const cameraLight = new THREE.DirectionalLight(0xffffff, 1);
        cameraLight.position.set(0, 0, 0); // Light originates from the camera
        this.camera.add(cameraLight);     // Attach the light to the camera
        this.scene.add(this.camera);      // Ensure camera (with light) is added to the scene





        // Add build plate for Ender 3 (220x220 mm)
        // In viewer units, your STL is scaled by 0.01, so 220mm becomes 2.2
        const plateSizeMM = 220;
        const plateSize = plateSizeMM * 0.01; // 2.2 in scene units
        const plateThickness = 0.005; // 5mm -> 0.05 scaled down

        const plateGeometry = new THREE.BoxGeometry(plateSize, plateThickness, plateSize);
        const plateMaterial = new THREE.MeshStandardMaterial({
            color: 0xcccccc,
            metalness: 0.1,
            roughness: 0.8
        });
        const plateMesh = new THREE.Mesh(plateGeometry, plateMaterial);

        // Move it slightly down so model sits on top
        plateMesh.position.y = -plateThickness / 2;
        this.scene.add(plateMesh);
        // Add a small arrow (cone) to indicate front on the build plate

        const arrowHeight = 0.05;
        const arrowRadius = 0.02;
        const coneGeometry = new THREE.ConeGeometry(arrowRadius, arrowHeight, 16);
        const coneMaterial = new THREE.MeshStandardMaterial({ color: 0xff0000 }); // red

        const frontMarker = new THREE.Mesh(coneGeometry, coneMaterial);

        // Position at the front center of the plate (assuming front is +Z)
        frontMarker.position.set(0, arrowHeight / 2, plateSize / 2 + arrowRadius);

        // Rotate so it points forward along +Z (cones point up by default, so rotate 90 degrees on X)
        frontMarker.rotation.x = Math.PI / 2;

        this.scene.add(frontMarker);




        this.loadGeometry();
        this.animate();
    }

    loadGeometry() {
        const { url, geometry } = this.props;
        const material = new THREE.MeshStandardMaterial({ color: 0x606060 });

        const loadMesh = (geom) => {
            const mesh = new THREE.Mesh(geom, material);

            mesh.scale.set(0.01, 0.01, 0.01);
            mesh.rotation.x = (-Math.PI / 2);
            mesh.rotation.y = 0;
            mesh.rotation.z = 0;

            // Compute bounding box AFTER scale and rotation
            geom.computeBoundingBox();
            const box = new THREE.Box3().setFromObject(mesh);

            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());

            // Move mesh so it is centered on the build plate
            mesh.position.x -= center.x;
            mesh.position.y -= box.min.y;  // put bottom at y=0
            mesh.position.z -= center.z;

            this.scene.add(mesh);
            this.mesh = mesh;
        };

        if (geometry) {
            loadMesh(geometry);
        } else if (url) {
            const loader = new STLLoader();
            loader.load(url, (geom) => {
                loadMesh(geom);
            });
        }
    }

    clearScene() {
        if (this.mesh) {
            this.scene.remove(this.mesh);
            this.mesh.geometry.dispose();
            this.mesh.material.dispose();
            this.mesh = null;
        }
    }
    handlePan = (dx, dy) => {
        const panSpeed = 0.2;

        // Get camera right and up vectors
        const direction = new THREE.Vector3();
        const right = new THREE.Vector3();
        const up = new THREE.Vector3();

        this.camera.getWorldDirection(direction); // Forward vector
        right.crossVectors(direction, this.camera.up).normalize(); // Right vector
        up.copy(this.camera.up).normalize(); // Up vector

        const panOffset = new THREE.Vector3();
        panOffset
            .addScaledVector(right, dx * panSpeed)
            .addScaledVector(up, dy * panSpeed);

        this.camera.position.add(panOffset);
    };
    handleRotateLeft = () => {
        if (this.mesh) {
            this.mesh.rotation.y += THREE.MathUtils.degToRad(10);
        }
    };

    handleRotateRight = () => {
        if (this.mesh) {
            this.mesh.rotation.y -= THREE.MathUtils.degToRad(10);
        }
    };

    handleRotateUp = () => {
        if (this.mesh) {
            this.mesh.rotation.x += THREE.MathUtils.degToRad(10);
        }
    };

    handleRotateDown = () => {
        if (this.mesh) {
            this.mesh.rotation.x -= THREE.MathUtils.degToRad(10);
        }
    };
    handleMoveLeft = () => {
        if (this.mesh) {
            this.mesh.position.x -= 0.1; // move left
        }
    };

    handleMoveRight = () => {
        if (this.mesh) {
            this.mesh.position.x += 0.1; // move right
        }
    };

    handleMoveUp = () => {
        if (this.mesh) {
            this.mesh.position.y += 0.1; // move up
        }
    };

    handleMoveDown = () => {
        if (this.mesh) {
            this.mesh.position.y -= 0.1; // move down
        }
    };
    animate = () => {
        requestAnimationFrame(this.animate);
        if (this.controls) this.controls.update();
        if (this.renderer && this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera);
        }
    };

    handleZoomIn = () => {
        this.camera.position.z = Math.max(0.1, this.camera.position.z - 0.5);
    };

    handleZoomOut = () => {
        this.camera.position.z += 0.5;
    };

    render() {
        let height = "250px"
        return (
            <div style={{   width: '100%',
    height: 'min(50vh, calc(100vh - 200px))',
    position: 'relative',
    boxSizing: 'border-box'}}>
                {/* This div hosts the 3D canvas */}
                <div
                    ref={this.mountRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                    }}
                ></div>

                {/* Controls Overlay */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '10px',
                        zIndex: 10,
                        paddingRight: "40px"

                    }}
                >
                    {/* Directional Buttons */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '36px',
                            gridTemplateRows: 'repeat(3, 36px)',
                            alignItems: 'center',
                            justifyItems: 'center',
                            gap: '2px',
                        }}
                    >
                        <button style={styles.zoomButton} onClick={this.handleRotateUp} title="Rotate Up">↑</button>
                        <div style={{ display: 'flex', gap: '2px' }}>
                            <button style={styles.zoomButton} onClick={this.handleRotateLeft} title="Rotate Left">←</button>
                            <button style={styles.zoomButton} onClick={this.handleRotateRight} title="Rotate Right">→</button>
                        </div>
                        <button style={styles.zoomButton} onClick={this.handleRotateDown} title="Rotate Down">↓</button>
                    </div>
                    {/* Move Buttons */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '36px',
                        gridTemplateRows: 'repeat(3, 36px)',
                        alignItems: 'center',
                        justifyItems: 'center',
                        gap: '2px',
                        marginLeft: '40px'
                    }}>
                        <button style={styles.zoomButton} onClick={this.handleMoveUp} title="Move Up">⇧</button>
                        <div style={{ display: 'flex', gap: '2px' }}>
                            <button style={styles.zoomButton} onClick={this.handleMoveLeft} title="Move Left">⇦</button>
                            <button style={styles.zoomButton} onClick={this.handleMoveRight} title="Move Right">⇨</button>
                        </div>
                        <button style={styles.zoomButton} onClick={this.handleMoveDown} title="Move Down">⇩</button>
                    </div>
                    {/* Zoom Buttons */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '36px',
                        gridTemplateRows: 'repeat(3, 36px)',
                        alignItems: 'center',
                        justifyItems: 'center',
                        gap: '2px',
                        marginLeft: '20px',
                        marginTop: "18px"
                    }}>                        <button onClick={this.handleZoomIn} style={styles.zoomButton} title="Zoom In">+</button>
                        <button onClick={this.handleZoomOut} style={styles.zoomButton} title="Zoom Out">–</button>
                    </div>
                </div>

            </div>
        );
    }
}

const styles = {
    zoomButton: {
        backgroundColor: '#ffffffcc',
        border: '1px solid #ccc',
        borderRadius: '6px',
        width: '36px',
        height: '36px',
        fontSize: '20px',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s',
        userSelect: 'none',
    },
};



export default STLViewer;